(function () {
  if (window.__algoliaRecommendLoaded) return;
  window.__algoliaRecommendLoaded = true;

  const SELECTOR_WIDGET = '[data-algolia-recommend]';
  const SELECTOR_CONFIG = '[data-algolia-recommend-config]';
  const SELECTOR_GRID = '.algolia-looking-similar__grid';
  const SELECTOR_CARD = '.algolia-looking-similar__card';
  const SELECTOR_CARD_LINK = '.algolia-looking-similar__card-link';
  const SELECTOR_SECONDARY_IMG = '.algolia-looking-similar__card-img--secondary';

  function init() {
    document.querySelectorAll(SELECTOR_WIDGET).forEach((sectionEl) => {
      const configEl = sectionEl.querySelector(SELECTOR_CONFIG);
      if (!configEl) return;
      let config;
      try {
        config = JSON.parse(configEl.textContent);
      } catch (err) {
        console.warn('[Algolia Recommendations] invalid config JSON', err);
        return;
      }
      setupWidget(sectionEl, config);
    });
  }

  async function setupWidget(sectionEl, config) {
    const gridEl = sectionEl.querySelector(SELECTOR_GRID);
    if (!gridEl) return;

    const algoliaConfig = window.algoliaShopify.config;
    const appId = algoliaConfig.app_id;
    const apiKey = algoliaConfig.search_api_key;
    const indexName = algoliaConfig.index_prefix + (config.indexNameSuffix || 'products');

    try {
      const res = await fetch(`https://${appId}-dsn.algolia.net/1/indexes/*/recommendations`, {
        method: 'POST',
        headers: {
          'X-Algolia-Application-Id': appId,
          'X-Algolia-API-Key': apiKey,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          requests: [{
            indexName,
            model: config.model,
            objectID: config.objectID,
            maxRecommendations: config.maxRecommendations,
            threshold: config.threshold,
            queryParameters: {
              filters: config.filters,
              clickAnalytics: true,
              analytics: true,
            },
            fallbackParameters: {
              filters: config.fallbackFilters,
              clickAnalytics: true,
              analytics: true,
            },
          }],
        }),
      });

      if (!res.ok) throw new Error(`Recommend API ${res.status}: ${await res.text()}`);

      const data = await res.json();
      const result = data.results?.[0];
      const hits = result?.hits ?? [];
      const queryID = result?.queryID;
      if (hits.length === 0) return;

      renderHits(gridEl, hits, queryID, indexName);
      attachHover(gridEl);
      attachClickTracking(gridEl);
      sectionEl.style.display = '';
      attachImpressionObserver(sectionEl, hits, indexName);
    } catch (err) {
      console.warn('[Algolia Recommendations]', err);
    }
  }

  function renderHits(gridEl, hits, queryID, indexName) {
    gridEl.innerHTML = hits.map((item, i) => {
      const url = '/products/' + item.handle;
      const img = item.image ?? item.featured_image ?? '';
      return `
        <div class="algolia-looking-similar__card" data-handle="${item.handle}">
          <a href="${url}" class="algolia-looking-similar__card-link"
             data-query-id="${queryID ?? ''}"
             data-object-id="${item.objectID ?? ''}"
             data-position="${i + 1}"
             data-index-name="${indexName}">
            <div class="algolia-looking-similar__card-img">
              <img class="algolia-looking-similar__card-img--primary" src="${img}" alt="${item.title}" loading="lazy" width="300" height="400">
              <img class="algolia-looking-similar__card-img--secondary" alt="" aria-hidden="true" loading="lazy" width="300" height="400">
            </div>
            <div class="algolia-looking-similar__card-body">
              <h3 class="algolia-looking-similar__card-title">${item.title}</h3>
              <span class="algolia-looking-similar__card-price">$${Number(item.price_min ?? item.price).toFixed(2)}</span>
            </div>
          </a>
        </div>`;
    }).join('');
  }

  function attachHover(gridEl) {
    const secondaryImageCache = new Map();
    const loadSecondaryImage = async (card) => {
      if (card.dataset.secondaryState) return;
      card.dataset.secondaryState = 'loading';
      const handle = card.dataset.handle;
      try {
        let secondSrc = secondaryImageCache.get(handle);
        if (secondSrc === undefined) {
          const res = await fetch(`/products/${handle}.js`, { headers: { Accept: 'application/json' } });
          if (!res.ok) throw new Error(`Product fetch ${res.status}`);
          const product = await res.json();
          const raw = product.images?.[1] ?? null;
          secondSrc = raw ? (raw.startsWith('//') ? `https:${raw}` : raw) : null;
          secondaryImageCache.set(handle, secondSrc);
        }
        if (!secondSrc) {
          card.dataset.secondaryState = 'none';
          return;
        }
        await new Promise((resolve, reject) => {
          const preload = new Image();
          preload.onload = resolve;
          preload.onerror = reject;
          preload.src = secondSrc;
        });
        const secondaryImg = card.querySelector(SELECTOR_SECONDARY_IMG);
        if (secondaryImg) {
          secondaryImg.src = secondSrc;
          card.dataset.secondaryLoaded = 'true';
          card.dataset.secondaryState = 'loaded';
        }
      } catch (err) {
        card.dataset.secondaryState = 'error';
      }
    };

    gridEl.querySelectorAll(SELECTOR_CARD).forEach((card) => {
      card.addEventListener('mouseenter', () => loadSecondaryImage(card), { once: true });
    });
  }

  function attachClickTracking(gridEl) {
    gridEl.addEventListener('click', (e) => {
      const link = e.target.closest(SELECTOR_CARD_LINK);
      if (!link) return;
      const qid = link.dataset.queryId;
      const oid = link.dataset.objectId;
      const pos = Number(link.dataset.position);
      const idx = link.dataset.indexName;
      if (!oid || !idx) return;

      if (qid) {
        window.aa('clickedObjectIDsAfterSearch', {
          index: idx,
          eventName: 'Recommendation Clicked',
          queryID: qid,
          objectIDs: [oid],
          positions: [pos || 1],
        });
      } else {
        window.aa('clickedObjectIDs', {
          index: idx,
          eventName: 'Recommendation Clicked',
          objectIDs: [oid],
        });
      }
    });
  }

  function attachImpressionObserver(sectionEl, hits, indexName) {
    const impressionPayload = {
      index: indexName,
      eventName: 'Recommendation Viewed',
      objectIDs: hits.map((h) => h.objectID).filter(Boolean),
    };
    const observer = new IntersectionObserver((entries, obs) => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          window.aa('viewedObjectIDs', impressionPayload);
          obs.disconnect();
          break;
        }
      }
    }, { threshold: 0.5 });
    observer.observe(sectionEl);
  }

  if (window.algoliaShopify?.config) {
    init();
  } else {
    document.addEventListener('algolia.hooks.initialize', init, { once: true });
  }
})();
